import {useDispatch, useSelector} from "react-redux";
import useToast from "../../../util/useToast";
import {
  editorConfigSelector,
  rxHidePortfolioTransactionEditor,
  rxShowHideDeleteConfirmationModal,
  rxUpdatePortfolioTransactions
} from "../../../slices/portfolioSlice";
import {useCallback, useEffect, useState} from "react";
import {AMOUNT_REGEX, MODE_EDIT, PORTFOLIO_TRANSACTION_TYPE_BUY} from "../../../codes";
import moment from "moment";
import {useTranslation} from "react-i18next";
import {
  createPortfolioTransaction,
  deletePortfolioTransaction,
  updatePortfolioTransaction
} from "../../../api/ApiPortfolioTransactions";
import {getOrDefault} from "../../../util/util";
import {selectAllAccounts} from "../../../slices/accountsSlice";
import {selectAllCategories} from "../../../slices/categoriesSlice";
import {appConfigSelector} from "../../../slices/appSlice";
import {selectTransactionById} from "../../../slices/transactionsSlice";
import useNetwork from "../../../util/useNetwork";
import {selectAllQuotes} from "../../../slices/quotesSlice";
import useValidator from "../../../util/useValidator";

const usePortfolioTransactionEditor = () => {
  const dispatch = useDispatch();

  const {t} = useTranslation("portfolio");
  const {showToastSuccess} = useToast();
  const {handleNetworkError} = useNetwork();
  const validate = useValidator();

  const {show, mode, transaction, deleteConfirmationModal} = useSelector(editorConfigSelector);
  const {defaultPortfolioAccount, defaultPortfolioTransactionCategory} = useSelector(appConfigSelector);

  const quotes = useSelector(selectAllQuotes);
  const accounts = useSelector(selectAllAccounts);
  const transactionCategories = useSelector(selectAllCategories);

  const portfolioTransactionTransaction = useSelector(s => selectTransactionById(s, transaction?.transaction));

  const [editedTransaction, setEditedTransaction] = useState({
    errors: {},
    type: PORTFOLIO_TRANSACTION_TYPE_BUY,
    symbol: "",
    price: "",
    quantity: "",
    valueDate: "",
    fees: "",
    note: ""
  });

  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);

  const hidePortfolioTransactionEditor = () => dispatch(rxHidePortfolioTransactionEditor());

  useEffect(() => {
    if (!transaction || !show) {
      return;
    }

    const values = {
      errors: {},
      account: mode === MODE_EDIT ? getOrDefault(portfolioTransactionTransaction?.account, null) : defaultPortfolioAccount,
      category: mode === MODE_EDIT ? getOrDefault(portfolioTransactionTransaction?.category, null) : defaultPortfolioTransactionCategory,
      type: getOrDefault(transaction.type, PORTFOLIO_TRANSACTION_TYPE_BUY),
      symbol: getOrDefault(transaction.symbol, ""),
      price: "" + getOrDefault(transaction.price, ""),
      fees: "" + getOrDefault(transaction.fees, ""),
      quantity: "" + getOrDefault(transaction.quantity, ""),
      valueDate: getOrDefault(transaction.valueDate, moment().format("YYYY-MM-DD")),
      note: getOrDefault(transaction.note, "")
    };

    setEditedTransaction(values);

    const res = [{id: null, name: t("transaction_editor_no_account_option")}];

    accounts.filter(a => a.active || a.id === defaultPortfolioAccount).forEach(a => res.push(a));

    setAvailableAccounts(res);

    setAvailableCategories(() => transactionCategories.filter(c => c.active || c.id === defaultPortfolioTransactionCategory));
  }, [show, mode, t, transaction, accounts, transactionCategories, portfolioTransactionTransaction, defaultPortfolioAccount, defaultPortfolioTransactionCategory]);

  const getErrors = values => {
    const errors = {};

    if (values.symbol.length === 0) {
      errors.symbol = "transaction_editor_error_symbol_must_be_filled";
    }

    if (values.price === undefined) {
      errors.price = "transaction_editor_error_price_must_be_filled";
    }

    if (values.quantity === undefined || +values.quantity <= 0) {
      errors.quantity = "transaction_editor_error_quantity_must_be_filled";
    }

    if (!/^\d\d\d\d-\d\d-\d\d$/.test(values.valueDate)) {
      errors.valueDate = "transaction_editor_error_date_must_be_filled";
    }

    return errors;
  };

  const submit = e => {
    e.preventDefault();

    let price, fees, quantity;

    if (editedTransaction.price.length) {
      price = +editedTransaction.price.replace(",", ".");
    }

    if (editedTransaction.fees.length) {
      fees = +editedTransaction.fees.replace(",", ".");
    }

    if (editedTransaction.quantity.length) {
      quantity = +editedTransaction.quantity.replace(",", ".");
    }

    const values = {
      account: editedTransaction.account,
      category: editedTransaction.category,
      type: editedTransaction.type,
      symbol: editedTransaction.symbol,
      price,
      quantity,
      fees,
      valueDate: editedTransaction.valueDate,
      note: editedTransaction.note
    };

    return validate(getErrors(values))
      .then(() => {
        if (mode === MODE_EDIT) {
          return updatePortfolioTransaction(transaction.id, values);
        } else {
          return createPortfolioTransaction(values);
        }
      })
      .then(result => dispatch(rxUpdatePortfolioTransactions(result)))
      .then(() => {
        showToastSuccess(t(mode === MODE_EDIT ? "transaction_edited" : "transaction_added"));

        hidePortfolioTransactionEditor();
      })
      .catch(error => {
        handleNetworkError(error, {
          400: err => setEditedTransaction(prev => ({...prev, errors: err.response.data}))
        });
      });
  };

  const updateEditedTransaction = field => event => {
    const values = {};

    switch (field) {
      case "price": {
        const price = event.target.value;

        if (!AMOUNT_REGEX.test(price)) {
          return;
        }

        values.price = price;

        break;
      }
      case "fees": {
        const fees = event.target.value;

        if (!AMOUNT_REGEX.test(fees)) {
          return;
        }

        values.fees = fees;

        break;
      }
      case "quantity": {
        const quantity = event.target.value;

        if (!/^(\d+[.,]?\d*)?$/.test(quantity)) {
          return;
        }

        values.quantity = quantity;

        break;
      }
      case "symbol":
        values.symbol = event.target.value;
        values.price = "" + (quotes.find(s => s.symbol === values.symbol) || {price: ""}).price;
        break;
      case "account":
      case "category":
      case "type":
      case "valueDate":
        values[field] = event;
        break;
      default:
        values[field] = event.target.value;
        break;
    }

    setEditedTransaction(prev => ({...prev, ...values}));
  };

  const showDeleteConfirmationModal = useCallback((() => dispatch(rxShowHideDeleteConfirmationModal(true))), [dispatch]);
  const hideDeleteConfirmationModal = useCallback((() => dispatch(rxShowHideDeleteConfirmationModal(false))), [dispatch]);

  const onDeleteTransaction = () => {
    return deletePortfolioTransaction(transaction.id)
      .then(result => {
        dispatch(rxUpdatePortfolioTransactions(result));
        dispatch(rxHidePortfolioTransactionEditor());
      })
      .catch(handleNetworkError)
      .finally(hideDeleteConfirmationModal);
  };

  return {
    show,
    t,
    mode,
    submit,
    hidePortfolioTransactionEditor,
    transaction,
    editedTransaction,
    updateEditedTransaction,
    deleteConfirmationModal,
    showDeleteConfirmationModal,
    hideDeleteConfirmationModal,
    onDeleteTransaction,
    availableAccounts,
    availableCategories
  };
};

export default usePortfolioTransactionEditor;
